import React, { useState, useEffect } from "react";
import { fetchServices } from "../api/services";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Facebook,
  Instagram,
  Mail,
  Phone,
  ShieldCheck,
  Truck,
  Youtube,
} from "lucide-react";
import janakLogo from "../assets/janak.png";
import { Heart, MapPin } from "lucide-react";

const COLORS = {
  primary: "#3AAFA9",
  textMain: "#111827", // Almost black for headings
  textSub: "#374151",  // Dark gray for regular text
  textMuted: "#6B7280", // Medium gray for bottom copyright
  line: "#E6E8EC",
  error: "#EF4444",
};

const trustPills = [

  "Secure Patient Data",
  "On-Time Delivery",
  "24x7 Care Support",
];

const socialLinks = [
  {
    icon: Facebook,
    href: "https://www.facebook.com/share/19QndN189Z/",
    label: "Facebook",
  },
  {
    icon: Instagram,
    href: "https://www.instagram.com/",
    label: "Instagram",
  },
  {
    icon: Youtube,
    href: "https://www.youtube.com/@ajay_vijaychouhan",
    label: "YouTube",
  },
];

const Footer = () => {
  const [footerServices, setFooterServices] = useState([]);
  
  useEffect(() => {
    fetchServices()
      .then((data) => setFooterServices(data.slice(0, 6)))
      .catch((err) => console.error("Failed to load footer services:", err));
  }, []);

  return (
    <footer className="relative overflow-hidden bg-white">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 12% 18%, rgba(58,175,169,0.12), transparent 42%), radial-gradient(circle at 85% 84%, rgba(58,175,169,0.1), transparent 40%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 pb-8 pt-16 md:pt-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75 }}
          viewport={{ once: true }}
          className="rounded-3xl border bg-white p-7 shadow-[0_20px_60px_rgba(58,175,169,0.15)] md:p-10"
          style={{ borderColor: COLORS.line }}
        >
          <div className="grid items-center gap-8 lg:grid-cols-[1.45fr_1fr]">
            <div>
              <div
                className="mb-4 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold tracking-[0.14em] uppercase"
                style={{
                  borderColor: `${COLORS.primary}70`,
                  backgroundColor: `${COLORS.primary}14`,
                  color: COLORS.primary,
                }}
              >
                Premium Care Network
              </div>
              <h2
                className="text-3xl font-extrabold leading-tight md:text-4xl"
                style={{ color: COLORS.textMain }}
              >
                Trusted home healthcare with a premium support experience.
              </h2>
              <p
                className="mt-4 max-w-2xl text-sm leading-relaxed md:text-base"
                style={{ color: COLORS.textSub }}
              >
                From routine health visits to guided chronic care, every
                interaction is designed for confidence, care, and reliability.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5"
                style={{ backgroundColor: COLORS.primary }}
              >
                Contact Care Team
                <ArrowRight
                  size={16}
                  className="transition group-hover:translate-x-1"
                />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center rounded-xl border px-6 py-3 text-sm font-semibold transition-all duration-300"
                style={{
                  borderColor: COLORS.line,
                  color: COLORS.textMain,
                  backgroundColor: "#FFFFFF",
                }}
              >
                Explore Services
              </Link>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-2.5">
            {trustPills.map((pill) => (
              <div
                key={pill}
                className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium"
                style={{
                  borderColor: COLORS.line,
                  backgroundColor: "#FFFFFF",
                  color: COLORS.textSub,
                }}
              >
                <BadgeCheck size={14} style={{ color: COLORS.primary }} />
                {pill}
              </div>
            ))}
          </div>
        </motion.div>

      <motion.div
  initial={{ opacity: 0, y: 30 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.15, duration: 0.75 }}
  viewport={{ once: true }}
  className="mt-10 grid gap-10 border-b pb-10 md:grid-cols-2 lg:grid-cols-4"
  style={{ borderColor: COLORS.line }}
>

  {/* JANAK INFO */}
  <div>
    <h2 className="text-2xl font-bold mb-4" style={{ color: COLORS.textMain }}>
      JANAK
    </h2>

    <p className="text-sm leading-relaxed max-w-xs" style={{ color: COLORS.textSub }}>
      Joint Assurance Network for Aging Kin. Bringing peace of mind through trusted local care partnerships.
    </p>

 <div className="mt-5 flex items-center gap-2 text-sm font-medium" style={{ color: COLORS.primary }}>
  <Heart size={16} />
  <span>Care That Stands In When You Can't</span>
</div>

  </div>

  {/* QUICK LINKS */}
  <div>
    <h3 className="text-sm font-bold uppercase mb-4" style={{ color: COLORS.textMain }}>
      Quick Links
    </h3>
    <div className="space-y-3 text-sm font-medium" style={{ color: COLORS.textSub }}>
  <Link to="/" className="block hover:text-[#3AAFA9] transition">
    Home
  </Link>
  <Link to="/how-it-work" className="block hover:text-[#3AAFA9] transition">
    How It Works
  </Link>
  <Link to="/services" className="block hover:text-[#3AAFA9] transition">
    Services
  </Link>
  <Link to="/subscription" className="block hover:text-[#3AAFA9] transition">
    Pricing
  </Link>
  <Link to="/safety" className="block hover:text-[#3AAFA9] transition">
    Safety & Compliance
  </Link>
  <Link to="/about" className="block hover:text-[#3AAFA9] transition">
    About Us
  </Link>
</div>

  </div>

  {/* OUR SERVICES */}
  <div>
    <h3 className="text-sm font-bold uppercase mb-4" style={{ color: COLORS.textMain }}>
      Our Services
    </h3>
    <div className="space-y-3 text-sm font-medium" style={{ color: COLORS.textSub }}>
      {footerServices.length > 0 ? (
        footerServices.map((service, idx) => (
          <Link key={idx} to="/services" className="block transition hover:text-[#3AAFA9]">
            {service.name}
          </Link>
        ))
      ) : (
        <p>Loading...</p>
      )}
    </div>
  </div>

  {/* CONTACT */}
  <div>
    <h3 className="text-sm font-bold uppercase mb-4" style={{ color: COLORS.textMain }}>
      Contact Us
    </h3>

    <div className="space-y-4 text-sm font-medium" style={{ color: COLORS.textSub }}>
      <div className="flex items-start gap-3">
        <Phone size={16} style={{ color: COLORS.primary }} />
        <div>
          <a href="tel:+919098114568" className="hover:text-[#3AAFA9] transition">
            +91 90981 14568
          </a>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Mail size={16} style={{ color: COLORS.primary }} />
        <a href="mailto:contact@thejanak.com" className="hover:text-[#3AAFA9] transition">
          contact@thejanak.com
        </a>
      </div>
    </div>
  </div>
</motion.div>


        <div
          className="flex flex-col items-center justify-between gap-4 pt-6 text-sm font-medium md:flex-row"
          style={{ color: COLORS.textSub }}
        >
          <p>&copy; {new Date().getFullYear()} Janak. All rights reserved.</p>
          <div className="flex items-center flex-wrap justify-center gap-4 mt-4 md:mt-0">
            <Link to="/privacy" className="font-semibold transition hover:text-[#3AAFA9]">
              Privacy Policy
            </Link>
            <Link to="/terms" className="font-semibold transition hover:text-[#3AAFA9]">
              Terms & Conditions
            </Link>
            <Link to="/cancellation-refund" className="font-semibold transition hover:text-[#3AAFA9]">
              Cancellation & Refund Policy
            </Link>
            <Link to="/shipping-delivery" className="font-semibold transition hover:text-[#3AAFA9]">
              Shipping & Delivery
            </Link>
            <Link to="/disclaimer" className="font-semibold transition hover:text-[#3AAFA9]">
              Disclaimer
            </Link>
            <Link to="/account-deletion" className="font-semibold transition hover:text-[#3AAFA9]">
              Account Deletion Policy
            </Link>
          </div>
          <p>
            Design & Developed by{" "}
            <a
              href="https://yashsoftsolution.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold transition hover:text-[#3AAFA9]"
              style={{ color: COLORS.primary }}
            >
              YashSoftSolution
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
